import React, { useEffect, useRef, useState } from 'react';
import s from './PromtBlocks.module.css';
import { useAppDispatch } from '../../../../store/store';
import { updateText } from '../../../../store/slices/imageSlice';
import { useSelector } from 'react-redux';
import { selectSelectedText } from '../../../../store/selectors/imageSelectors';
import { InlineText, CanvasColor } from '../../../../shared/types/imagesType';
import Slider from '../../../../shared/ui/slider/Slider';
import { RgbaColor } from 'react-colorful';

import { selectActiveSettingsSlider } from '../../../../store/selectors/pageSelectors';
import ColorPickerField from '../../../../shared/ui/color-picker/ColorPickerField';
import { AnimatedContainer } from '../../../../shared/ui/animated-container/AimatedContainer';

export const rgbaToCss = (color: RgbaColor) =>
    `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
export const canvasColorToCss = (color: CanvasColor) =>
    `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.transparency})`;

const FONT_FAMILIES = [
    'Courier',
    'Cal Sans',
    'Oswald',
    'Arial',
    'Times New Roman',
    // 'Georgia',
    // 'Notable',
    // 'Rye',
    // 'Nunito Sans',
];
const PRESET_COLORS = [
    { r: 255, g: 221, b: 53, a: 1 },
    { r: 21, g: 101, b: 192, a: 1 },
    { r: 241, g: 67, b: 54, a: 1 },
    { r: 114, g: 216, b: 165, a: 1 },
    { r: 223, g: 98, b: 150, a: 1 },
    { r: 1, g: 1, b: 1, a: 1 },
];

type TEXT_STYLES_TYPE = {
    id: string;
    label: string;
    preview: string;
    effect: {
        stroke: number;
        stroke_color: RgbaColor;
        color: CanvasColor;
        text_shadow_offset_x: number;
        text_shadow_offset_y: number;
        text_shadow_blur: number;
        text_shadow_color: RgbaColor;
        background_radius: number;
        background_width: number;
        background_height: number;
        background_color: RgbaColor;
        style: string;
    };
};
const TEXT_STYLES: TEXT_STYLES_TYPE[] = [
    {
        id: 'none',
        label: 'None',
        preview: 'Aa',
        effect: {
            stroke: 0,
            stroke_color: { r: 0, g: 0, b: 0, a: 0 },
            color: { red: 1, green: 1, blue: 1, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 0,
            text_shadow_color: { r: 0, g: 0, b: 0, a: 0 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 0, g: 0, b: 0, a: 0 },
            style: 'none',
        },
    },
    {
        id: 'effect1',
        label: 'Effect 1',
        preview: 'Aa',
        effect: {
            stroke: 1,
            stroke_color: { r: 1, g: 1, b: 1, a: 1 },
            color: { red: 255, green: 191, blue: 0, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 0,
            text_shadow_color: { r: 0, g: 0, b: 0, a: 1 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 255, g: 255, b: 255, a: 0 },
            style: 'none',
        },
    },
    {
        id: 'effect2',
        label: 'Effect 2',
        preview: 'Aa',
        effect: {
            stroke: 1,
            stroke_color: { r: 0, g: 0, b: 0, a: 1 },
            color: { red: 255, green: 255, blue: 255, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 0,
            text_shadow_color: { r: 255, g: 255, b: 255, a: 0.8 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 255, g: 255, b: 255, a: 0 },
            style: 'none',
        },
    },

    {
        id: 'effect3',
        label: 'Effect 3',
        preview: 'Aa',
        effect: {
            stroke: 0,
            stroke_color: { r: 0, g: 0, b: 0, a: 0 },
            color: { red: 243, green: 163, blue: 206, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 3,
            text_shadow_offset_y: 2,
            text_shadow_blur: 1,
            text_shadow_color: { r: 108, g: 46, b: 103, a: 1 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 0, g: 0, b: 0, a: 0 },
            style: 'none',
        },
    },

    {
        id: 'effect4',
        label: 'Effect 4',
        preview: 'Aa',
        effect: {
            stroke: 0,
            stroke_color: { r: 0, g: 0, b: 0, a: 0 },
            color: { red: 255, green: 255, blue: 255, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 2,
            text_shadow_offset_y: 2,
            text_shadow_blur: 10,
            text_shadow_color: { r: 255, g: 214, b: 92, a: 0.6 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 240, g: 240, b: 240, a: 0 },
            style: 'none',
        },
    },

    // {
    //     id: 'effect5',
    //     label: 'Effect 5',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 0,
    //         stroke_color: { r: 80, g: 40, b: 0, a: 1 },
    //         color: { red: 80, green: 40, blue: 0, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 0,
    //         text_shadow_offset_y: 0,
    //         text_shadow_blur: 0,
    //         text_shadow_color: { r: 0, g: 0, b: 0, a: 0.5 },
    //         background_radius: 8,
    //         background_width: 10,
    //         background_height: 10,
    //         background_color: { r: 255, g: 255, b: 255, a: 1 },
    //         style: 'none',
    //     },
    // },

    // {
    //     id: 'effect6',
    //     label: 'Effect 6',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 0,
    //         stroke_color: { r: 0, g: 0, b: 0, a: 0 },
    //         color: { red: 245, green: 245, blue: 245, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 0,
    //         text_shadow_offset_y: 0,
    //         text_shadow_blur: 1,
    //         text_shadow_color: { r: 50, g: 50, b: 50, a: 0.4 },
    //         background_radius: 4,
    //         background_width: 15,
    //         background_height: 15,
    //         background_color: { r: 30, g: 30, b: 30, a: 1 },
    //         style: 'none',
    //     },
    // },

    {
        id: 'effect7',
        label: 'Effect 7',
        preview: 'Aa',
        effect: {
            stroke: 1,
            stroke_color: { r: 255, g: 69, b: 0, a: 1 },
            color: { red: 255, green: 140, blue: 0, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 15,
            text_shadow_color: { r: 255, g: 215, b: 0, a: 0.6 },
            background_radius: 6,
            background_width: 15,
            background_height: 10,
            background_color: { r: 255, g: 240, b: 220, a: 1 },
            style: 'none',
        },
    },

    {
        id: 'effect8',
        label: 'Effect 8',
        preview: 'Aa',
        effect: {
            stroke: 1,
            stroke_color: { r: 75, g: 0, b: 130, a: 1 },
            color: { red: 255, green: 165, blue: 0, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 3,
            text_shadow_offset_y: 3,
            text_shadow_blur: 0,
            text_shadow_color: { r: 128, g: 0, b: 128, a: 0.5 },
            background_radius: 10,
            background_width: 20,
            background_height: 10,
            background_color: { r: 240, g: 230, b: 250, a: 1 },
            style: 'none',
        },
    },
    // {
    //     id: 'effect9',
    //     label: 'Effect 9',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 0,
    //         stroke_color: { r: 0, g: 0, b: 0, a: 0 },
    //         color: { red: 255, green: 0, blue: 50, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 2,
    //         text_shadow_offset_y: 0,
    //         text_shadow_blur: 0,
    //         text_shadow_color: { r: 0, g: 255, b: 200, a: 0.8 },
    //         background_radius: 0,
    //         background_width: 0,
    //         background_height: 0,
    //         background_color: { r: 0, g: 0, b: 0, a: 0 },
    //         style: 'none',
    //     },
    // },
    // {
    //     id: 'effect10',
    //     label: 'Effect 10',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 0,
    //         stroke_color: { r: 0, g: 0, b: 0, a: 0 },
    //         color: { red: 240, green: 240, blue: 240, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 1,
    //         text_shadow_offset_y: 1,
    //         text_shadow_blur: 2,
    //         text_shadow_color: { r: 100, g: 100, b: 100, a: 0.5 },
    //         background_radius: 4,
    //         background_width: 15,
    //         background_height: 15,
    //         background_color: { r: 30, g: 30, b: 30, a: 0.2 },
    //         style: 'none',
    //     },
    // },
    // {
    //     id: 'effect11',
    //     label: 'Effect 11',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 1,
    //         stroke_color: { r: 200, g: 200, b: 255, a: 1 },
    //         color: { red: 180, green: 180, blue: 200, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 0,
    //         text_shadow_offset_y: 0,
    //         text_shadow_blur: 10,
    //         text_shadow_color: { r: 220, g: 220, b: 255, a: 0.7 },
    //         background_radius: 8,
    //         background_width: 0,
    //         background_height: 0,
    //         background_color: { r: 10, g: 10, b: 30, a: 0 },
    //         style: 'none',
    //     },
    // },
    // {
    //     id: 'effect12',
    //     label: 'Effect 12',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 0,
    //         stroke_color: { r: 0, g: 0, b: 0, a: 0 },
    //         color: { red: 255, green: 255, blue: 255, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 4,
    //         text_shadow_offset_y: 0,
    //         text_shadow_blur: 8,
    //         text_shadow_color: { r: 255, g: 0, b: 150, a: 0.7 },
    //         background_radius: 6,
    //         background_width: 0,
    //         background_height: 0,
    //         background_color: { r: 30, g: 0, b: 60, a: 0 },
    //         style: 'none',
    //     },
    // },

    {
        id: 'effect13',
        label: 'Effect 13',
        preview: 'Aa',
        effect: {
            stroke: 1,
            stroke_color: { r: 180, g: 180, b: 255, a: 0.7 },
            color: { red: 220, green: 240, blue: 255, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 8,
            text_shadow_color: { r: 255, g: 0, b: 255, a: 0.6 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 0, g: 0, b: 0, a: 0 },
            style: 'none',
        },
    },
    {
        id: 'effect14',
        label: 'Effect 14',
        preview: 'Aa',
        effect: {
            stroke: 2,
            stroke_color: { r: 139, g: 28, b: 54, a: 1 },
            color: { red: 255, green: 0, blue: 153, transparency: 1, gradient: 'none' },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 14,
            text_shadow_color: { r: 255, g: 0, b: 153, a: 0.7 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 0, g: 0, b: 0, a: 0 },
            style: 'none',
        },
    },

    // {
    //     id: 'effect15',
    //     label: 'Effect 15',
    //     preview: 'Aa',
    //     effect: {
    //         stroke: 1,
    //         stroke_color: { r: 170, g: 255, b: 255, a: 1 },
    //         color: { red: 204, green: 255, blue: 255, transparency: 1, gradient: 'none' },
    //         text_shadow_offset_x: 0,
    //         text_shadow_offset_y: 0,
    //         text_shadow_blur: 6,
    //         text_shadow_color: { r: 238, g: 255, b: 255, a: 0.5 },
    //         background_radius: 10,
    //         background_width: 0,
    //         background_height: 0,
    //         background_color: { r: 238, g: 255, b: 255, a: 0 },
    //         style: 'none',
    //     },
    // },
];

const AddTextBlock: React.FC = () => {
    const selectedText = useSelector(selectSelectedText);
    const dispatch = useAppDispatch();
    const activeSettingsSlider = useSelector(selectActiveSettingsSlider);
    const [activeStyle, setActiveStyle] = useState(TEXT_STYLES[0].id);

    const [draft, setDraft] = useState<InlineText>({
        id: `${Date.now()}`,
        content: 'Add text',
        location: { x: 200, y: 200 },
        color: { red: 255, green: 255, blue: 255, transparency: 1, gradient: 'none' },
        size: 40,
        font: FONT_FAMILIES[0],
        rotation: 1,
        stroke: 0,
        stroke_color: { r: 255, g: 255, b: 255, a: 1 },
        text_shadow_offset_x: 0,
        text_shadow_offset_y: 0,
        text_shadow_blur: 0,
        text_shadow_color: { r: 0, g: 0, b: 0, a: 1 },
        background_radius: 0,
        background_width: 0,
        background_height: 0,
        background_color: { r: 255, g: 255, b: 255, a: 0 },
        style: 'none',
    });

    useEffect(() => {
        if (selectedText) {
            setDraft(selectedText);
        } else {
            setDraft((prev) => ({ ...prev, id: `${Date.now()}` }));
        }
    }, [selectedText]);

    const changeValue = <K extends keyof InlineText>(key: K, value: InlineText[K]) => {
        if (selectedText) {
            dispatch(updateText({ ...selectedText, [key]: value }));
        } else {
            setDraft((prev) => ({ ...prev, [key]: value }));
        }
    };

    const handleStyleSelect = (style: (typeof TEXT_STYLES)[0]) => {
        setActiveStyle(style.id);
        if (style.effect) {
            const {
                stroke,
                stroke_color,
                color,
                text_shadow_offset_x,
                text_shadow_offset_y,
                text_shadow_blur,
                text_shadow_color,
                background_radius,
                background_width,
                background_height,
                background_color,
            } = style.effect;

            if (selectedText) {
                dispatch(
                    updateText({
                        ...selectedText,
                        stroke: stroke,
                        stroke_color: stroke_color,
                        color: color,
                        text_shadow_offset_x: text_shadow_offset_x,
                        text_shadow_offset_y: text_shadow_offset_y,
                        text_shadow_blur: text_shadow_blur,
                        text_shadow_color: text_shadow_color,
                        background_radius: background_radius,
                        background_width: background_width,
                        background_height: background_height,
                        background_color: background_color,
                        style: 'none',
                    }),
                );
            } else {
                setDraft((prev) => ({
                    ...prev,
                    stroke: stroke,
                    stroke_color: stroke_color,
                    color: color,
                    text_shadow_offset_x: text_shadow_offset_x,
                    text_shadow_offset_y: text_shadow_offset_y,
                    text_shadow_blur: text_shadow_blur,
                    text_shadow_color: text_shadow_color,
                    background_radius: background_radius,
                    background_width: background_width,
                    background_height: background_height,
                    background_color: background_color,
                    style: 'none',
                }));
            }
        }
    };

    return (
        <AnimatedContainer className={s.container}>
            {activeSettingsSlider === 'fonts' && (
                <div className={s.block_container}>
                    <h3 className={s.title}>Fonts</h3>
                    <p className={s.subtitle}>Choose a font style for your text</p>
                    <div className={s.font_select_row}>
                        {FONT_FAMILIES.map((f) => (
                            <p
                                key={f}
                                className={`${draft.font === f ? s.active : ''} ${s.card}`}
                                style={{ fontFamily: f }}
                                onClick={() => changeValue('font', f)}
                            >
                                {f}
                            </p>
                        ))}
                    </div>
                </div>
            )}

            {activeSettingsSlider === 'style' && (
                <div className={s.block_container}>
                    <h3 className={s.title}>Styles</h3>
                    <p className={s.subtitle}>Choose a style for your text</p>
                    <div className={s.styles_grid}>
                        {TEXT_STYLES.map((style) => {
                            const previewColor = style.effect
                                ? canvasColorToCss(style.effect.color)
                                : 'black';
                            const previewStroke = style.effect ? style.effect.stroke : 0;
                            const previewStrokeColor = style.effect
                                ? rgbaToCss(style.effect.stroke_color)
                                : 'black';
                            const normalShadow = `${style.effect.text_shadow_offset_x}px ${style.effect.text_shadow_offset_y}px ${style.effect.text_shadow_blur}px ${rgbaToCss(style.effect.text_shadow_color)}`;
                            return (
                                <div
                                    key={style.id}
                                    className={`${activeStyle === style.id ? s.active : ''} ${s.card}`}
                                    // style={{
                                    //     width: '120px',
                                    //     height: '60px',
                                    // }}
                                    onClick={() => handleStyleSelect(style)}
                                >
                                    <span
                                        className={s.preview_text}
                                        style={{
                                            color: previewColor,
                                            WebkitTextStroke: `${previewStroke}px ${previewStrokeColor}`,
                                            textShadow: normalShadow,
                                        }}
                                    >
                                        {style.preview}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {activeSettingsSlider === 'size' && (
                <div className={s.block_container}>
                    <h3 className={s.title}>Size</h3>
                    <p className={s.subtitle}>Choose a size for your text</p>
                    <div className={s.slider_container}>
                        <Slider
                            min={10}
                            max={100}
                            value={draft.size}
                            className={s.slider}
                            onChange={(value) => changeValue('size', value)}
                        />
                        <p className={s.subtitle} style={{ width: '20px' }}>
                            {draft.size}
                        </p>
                    </div>
                </div>
            )}
            {activeSettingsSlider === 'color' && (
                <div className={s.block_container}>
                    <h3 className={s.title}>Color</h3>
                    <p className={s.subtitle}>Choose a color for your text</p>
                    <div className={s.color_container}>
                        {/* <ColorPickerField
                            color={{
                                r: draft.color.red,
                                g: draft.color.green,
                                b: draft.color.blue,
                                a: draft.color.transparency,
                            }}
                            onChange={(color: RgbaColor) =>
                                changeValue('color', {
                                    red: color.r,
                                    green: color.g,
                                    blue: color.b,
                                    transparency: color.a,
                                    gradient: 'none',
                                })
                            }
                        /> */}

                        {PRESET_COLORS.map((preset) => (
                            <button
                                key={`${preset.r}-${preset.g}-${preset.b}`}
                                onClick={() =>
                                    changeValue('color', {
                                        red: preset.r,
                                        green: preset.g,
                                        blue: preset.b,
                                        transparency: preset.a,
                                        gradient: 'none',
                                    })
                                }
                                className={s.color_btn}
                                style={{
                                    backgroundColor: `rgba(${preset.r},${preset.g},${preset.b},${preset.a})`,
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </AnimatedContainer>
    );
};

export default AddTextBlock;
